import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, LogOut, Users, Mail, Send, Search, Download, Phone, Clock, CheckCircle2, AlertCircle, MapPin, Upload, FileText } from "lucide-react";
import { AREAS } from "../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "modg_admin_token";
const Input = "w-full bg-white border border-black/20 px-4 py-3 text-base focus:outline-none focus:ring-1 focus:ring-[#0a0a0a] focus:border-[#0a0a0a] transition-colors";

const SOURCE_LABEL = {
  buyer_quiz: "Buyer Quiz",
  seller_quiz: "Seller Quiz",
  contact: "Contact Form",
  area_alert: "Area Alert",
  import: "Imported",
  home_valuation: "Home Valuation",
  guide_download: "Guide Download"
};

const leadArea = (l) => l.area || l.quiz_answers?.area || null;

// Map any selected city (region, "region, CA", or a surrounding city) back to its main region.
const regionOf = (areaValue) => {
  if (!areaValue) return null;
  const v = areaValue.trim();
  for (const a of AREAS) {
    if (v === a.name || v === `${a.name}, ${a.state}` || a.name === v.split(",")[0].trim()) return a.name;
    if (a.nearby && a.nearby.includes(v)) return a.name;
  }
  return null;
};

function Login({ onLogin }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a] px-6">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-sm bg-white p-10">
        <div className="w-12 h-12 bg-gold/10 flex items-center justify-center mb-6">
          <Lock className="text-gold" size={22} />
        </div>
        <h1 className="font-heading text-3xl font-light">Admin Access</h1>
        <p className="text-[#52525b] font-light text-sm mt-2 mb-8">Melissa O'Donnell Real Estate Group</p>
        <input
          data-testid="admin-password"
          type="password"
          className={Input}
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoFocus
        />
        <button data-testid="admin-login-submit" type="submit" disabled={loading} className="w-full bg-[#0a0a0a] text-white py-4 mt-4 text-sm tracking-wide hover:bg-[#27272a] transition-colors disabled:opacity-60">
          {loading ? "Signing in..." : "Sign In"}
        </button>
      </form>
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("leads");
  const [query, setQuery] = useState("");
  const [areaFilter, setAreaFilter] = useState("");
  const [emailConfigured, setEmailConfigured] = useState(false);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    try {
      const [l, s] = await Promise.all([
        axios.get(`${API}/admin/leads`, auth),
        axios.get(`${API}/admin/email-status`, auth)
      ]);
      setLeads(l.data);
      setEmailConfigured(s.data.configured);
    } catch (err) {
      if (err.response?.status === 401) { onLogout(); return; }
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    let list = leads;
    if (areaFilter) list = list.filter((l) => regionOf(leadArea(l)) === areaFilter);
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter((l) =>
        [l.name, l.email, l.phone, l.source, leadArea(l)].filter(Boolean).some((v) => v.toLowerCase().includes(q))
      );
    }
    return list;
  }, [leads, query, areaFilter]);

  const areaCounts = useMemo(() => {
    const buyerish = leads.filter((l) => l.source === "buyer_quiz" || l.source === "area_alert");
    return AREAS.map((a) => ({
      name: a.name,
      count: buyerish.filter((l) => regionOf(leadArea(l)) === a.name).length
    }));
  }, [leads]);

  const emails = useMemo(() => [...new Set(leads.map((l) => l.email).filter(Boolean))], [leads]);

  const exportCsv = () => {
    const rows = [["Name", "Email", "Phone", "Source", "Preferred Contact", "Preferred Time", "Message", "Date"]];
    leads.forEach((l) => rows.push([
      l.name, l.email || "", l.phone || "", SOURCE_LABEL[l.source] || l.source,
      l.preferred_contact || "", l.preferred_time || "", (l.message || "").replace(/\n/g, " "),
      new Date(l.created_at).toLocaleString()
    ]));
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = "leads.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const stats = {
    total: leads.length,
    buyers: leads.filter((l) => l.source === "buyer_quiz").length,
    sellers: leads.filter((l) => l.source === "seller_quiz").length,
    contacts: leads.filter((l) => l.source === "contact").length,
    guides: leads.filter((l) => l.source === "guide_download").length
  };

  return (
    <div className="min-h-screen bg-surface">
      <header className="bg-[#0a0a0a] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <div>
            <p className="font-heading text-xl">Melissa O'Donnell</p>
            <p className="eyebrow text-[10px] text-gold">Admin Dashboard</p>
          </div>
          <button data-testid="admin-logout" onClick={onLogout} className="flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors">
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-black/5 mb-10">
          {[
            { label: "Total Leads", value: stats.total },
            { label: "Buyer Quizzes", value: stats.buyers },
            { label: "Seller Quizzes", value: stats.sellers },
            { label: "Contact Forms", value: stats.contacts },
            { label: "Guide Downloads", value: stats.guides }
          ].map((s) => (
            <div key={s.label} className="bg-white p-6" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}>
              <p className="eyebrow text-[10px] text-[#a1a1aa]">{s.label}</p>
              <p className="font-heading text-4xl mt-2">{s.value}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 mb-6 border-b border-black/10">
          <button data-testid="tab-leads" onClick={() => setTab("leads")} className={`flex items-center gap-2 px-5 py-3 text-sm transition-colors border-b-2 -mb-px ${tab === "leads" ? "border-gold text-[#0a0a0a]" : "border-transparent text-[#52525b]"}`}>
            <Users size={16} /> Leads
          </button>
          <button data-testid="tab-compose" onClick={() => setTab("compose")} className={`flex items-center gap-2 px-5 py-3 text-sm transition-colors border-b-2 -mb-px ${tab === "compose" ? "border-gold text-[#0a0a0a]" : "border-transparent text-[#52525b]"}`}>
            <Mail size={16} /> Compose Email
          </button>
          <button data-testid="tab-import" onClick={() => setTab("import")} className={`flex items-center gap-2 px-5 py-3 text-sm transition-colors border-b-2 -mb-px ${tab === "import" ? "border-gold text-[#0a0a0a]" : "border-transparent text-[#52525b]"}`}>
            <Upload size={16} /> Import Contacts
          </button>
        </div>

        {loading ? (
          <p className="text-[#52525b] font-light py-20 text-center">Loading...</p>
        ) : tab === "leads" ? (
          <div data-testid="leads-panel">
            <div className="mb-6" data-testid="area-demand">
              <div className="flex items-center gap-2 mb-3">
                <MapPin size={16} className="text-gold" />
                <p className="eyebrow text-[10px] text-[#a1a1aa]">Buyer Demand by Area (quizzes + alerts)</p>
              </div>
              <div className="flex flex-wrap gap-3">
                <button
                  data-testid="area-chip-all"
                  onClick={() => setAreaFilter("")}
                  className={`px-5 py-3 border text-sm transition-all ${areaFilter === "" ? "border-[#0a0a0a] bg-[#0a0a0a] text-white" : "border-black/10 hover:border-gold bg-white"}`}
                >
                  All areas <span className="text-gold font-medium ml-1">{leads.length}</span>
                </button>
                {areaCounts.map((a) => (
                  <button
                    key={a.name}
                    data-testid={`area-chip-${a.name.toLowerCase().replace(/\s/g, "-")}`}
                    onClick={() => setAreaFilter(areaFilter === a.name ? "" : a.name)}
                    className={`px-5 py-3 border text-sm transition-all ${areaFilter === a.name ? "border-[#0a0a0a] bg-[#0a0a0a] text-white" : "border-black/10 hover:border-gold bg-white"}`}
                  >
                    {a.name} <span className="text-gold font-medium ml-1">{a.count}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-between mb-5">
              <div className="relative max-w-sm w-full">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#a1a1aa]" />
                <input data-testid="leads-search" className={`${Input} pl-9`} placeholder="Search name, email, phone..." value={query} onChange={(e) => setQuery(e.target.value)} />
              </div>
              <button data-testid="export-csv" onClick={exportCsv} className="flex items-center gap-2 border border-black/20 px-5 py-3 text-sm hover:bg-[#0a0a0a] hover:text-white transition-colors">
                <Download size={16} /> Export CSV
              </button>
            </div>

            <div className="bg-white border border-black/5 overflow-x-auto">
              <table className="w-full text-sm" data-testid="leads-table">
                <thead>
                  <tr className="text-left eyebrow text-[10px] text-[#a1a1aa] border-b border-black/5">
                    <th className="px-5 py-4 font-medium">Name</th>
                    <th className="px-5 py-4 font-medium">Contact</th>
                    <th className="px-5 py-4 font-medium">Source</th>
                    <th className="px-5 py-4 font-medium">Preference</th>
                    <th className="px-5 py-4 font-medium">Details</th>
                    <th className="px-5 py-4 font-medium">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr><td colSpan={6} className="px-5 py-16 text-center text-[#a1a1aa] font-light">No leads yet.</td></tr>
                  ) : filtered.map((l) => (
                    <tr key={l.id} className="border-b border-black/5 last:border-0 align-top hover:bg-surface" data-testid={`lead-row-${l.id}`}>
                      <td className="px-5 py-4 font-medium">{l.name}</td>
                      <td className="px-5 py-4 text-[#52525b]">
                        {l.email && <div className="flex items-center gap-1.5"><Mail size={13} className="text-gold" /> {l.email}</div>}
                        {l.phone && <div className="flex items-center gap-1.5 mt-1"><Phone size={13} className="text-gold" /> {l.phone}</div>}
                      </td>
                      <td className="px-5 py-4">
                        <span className="inline-block bg-surface border border-black/10 px-2.5 py-1 text-xs">{SOURCE_LABEL[l.source] || l.source}</span>
                      </td>
                      <td className="px-5 py-4 text-[#52525b]">
                        {l.preferred_contact && <div className="capitalize">{l.preferred_contact}</div>}
                        {l.preferred_time && <div className="flex items-center gap-1.5 mt-1 text-xs"><Clock size={12} /> {l.preferred_time}</div>}
                      </td>
                      <td className="px-5 py-4 text-[#52525b] max-w-xs">
                        {leadArea(l) && <p className="text-xs"><span className="text-[#a1a1aa]">area:</span> {leadArea(l)}</p>}
                        {l.message && <p className="italic">"{l.message}"</p>}
                        {l.quiz_answers && (
                          <ul className="text-xs space-y-0.5">
                            {Object.entries(l.quiz_answers).map(([k, v]) => <li key={k}><span className="text-[#a1a1aa]">{k}:</span> {v}</li>)}
                          </ul>
                        )}
                      </td>
                      <td className="px-5 py-4 text-[#a1a1aa] text-xs whitespace-nowrap">{new Date(l.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : tab === "compose" ? (
          <Compose token={token} emails={emails} configured={emailConfigured} />
        ) : (
          <ImportContacts token={token} onImported={load} />
        )}
      </div>
    </div>
  );
}

function Compose({ token, emails, configured }) {
  const [mode, setMode] = useState("all"); // all | single
  const [single, setSingle] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const recipients = mode === "all" ? emails : (single ? [single] : []);

  const useTemplate = () => {
    const month = new Date().toLocaleString("en-US", { month: "long" });
    setSubject(`Your ${month} Southern California Market Update 🏡`);
    setBody(
`Hi there,

Here's your monthly snapshot of the Redlands, Palm Springs & Temecula real estate market.

📈 MARKET UPDATE
• Median home price: $______
• Homes sold last month: ______
• Average days on market: ______

⭐ FEATURED LISTING
______ — ______, CA
__ bed / __ bath / ______ sqft — $______
A quick line about what makes this home special.

💡 TIP OF THE MONTH
Share a quick buying, selling, or refinancing tip here.

Thinking about making a move? Just reply to this email or give me a call — I'd love to help you or someone you know.

Warmly,
Melissa O'Donnell
Real Broker | DRE# 01810851`
    );
    toast.success("Monthly template loaded — just fill in the blanks!");
  };

  const send = async (e) => {
    e.preventDefault();
    if (recipients.length === 0) return toast.error("Select at least one recipient");
    if (!subject.trim()) return toast.error("Add a subject");
    if (!body.trim()) return toast.error("Write a message");
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/admin/send-email`, {
        recipients, subject, body: body.replace(/\n/g, "<br/>")
      }, auth);
      if (data.status === "not_configured") {
        toast.info(data.message);
      } else if (data.status === "error" || data.sent < data.recipients) {
        toast.warning(
          `Delivered ${data.sent} of ${data.recipients}. Sending to addresses other than your own requires verifying a domain at resend.com/domains.`,
          { duration: 8000 }
        );
        if (data.sent > 0) { setSubject(""); setBody(""); setSingle(""); }
      } else {
        toast.success(`Sent to ${data.sent} of ${data.recipients} recipient(s)`);
        setSubject(""); setBody(""); setSingle("");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-3xl" data-testid="compose-panel">
      <div className={`flex items-center gap-2 p-4 mb-6 text-sm border ${configured ? "border-green-200 bg-green-50 text-green-800" : "border-amber-200 bg-amber-50 text-amber-800"}`} data-testid="email-status-banner">
        {configured ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
        {configured
          ? "Email is connected. Alerts to your own inbox send instantly. To email other recipients (buyers on your list), verify a domain at resend.com/domains."
          : "Email sending is not enabled yet. Add your Resend API key to start sending — until then, messages won't be delivered."}
      </div>

      <form onSubmit={send} className="bg-white border border-black/5 p-8 space-y-5">
        <div>
          <p className="eyebrow text-[10px] text-[#a1a1aa] mb-3">Recipients</p>
          <div className="flex gap-3">
            <button type="button" data-testid="recipients-all" onClick={() => setMode("all")} className={`flex-1 p-4 border text-sm transition-all ${mode === "all" ? "border-[#0a0a0a] bg-[#0a0a0a] text-white" : "border-black/10 hover:border-gold"}`}>
              Everyone ({emails.length})
            </button>
            <button type="button" data-testid="recipients-single" onClick={() => setMode("single")} className={`flex-1 p-4 border text-sm transition-all ${mode === "single" ? "border-[#0a0a0a] bg-[#0a0a0a] text-white" : "border-black/10 hover:border-gold"}`}>
              One person
            </button>
          </div>
          {mode === "single" && (
            <select data-testid="single-recipient" className={`${Input} mt-3`} value={single} onChange={(e) => setSingle(e.target.value)}>
              <option value="">Select a contact...</option>
              {emails.map((e) => <option key={e} value={e}>{e}</option>)}
            </select>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-black/5 pt-5">
          <p className="text-sm font-light text-[#52525b]">Need a head start?</p>
          <button type="button" data-testid="use-template" onClick={useTemplate} className="inline-flex items-center gap-2 border border-black/20 px-4 py-2.5 text-sm hover:bg-[#0a0a0a] hover:text-white transition-colors">
            <FileText size={15} /> Use monthly newsletter template
          </button>
        </div>

        <input data-testid="email-subject" className={Input} placeholder="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
        <textarea data-testid="email-body" rows={9} className={`${Input} resize-none`} placeholder="Write your message..." value={body} onChange={(e) => setBody(e.target.value)} />

        <button data-testid="send-email" type="submit" disabled={sending} className="flex items-center justify-center gap-2 w-full bg-gold text-white py-4 text-sm tracking-wide hover:bg-gold-hover transition-colors disabled:opacity-60">
          <Send size={16} /> {sending ? "Sending..." : `Send to ${recipients.length} recipient(s)`}
        </button>
      </form>
    </div>
  );
}

function ImportContacts({ token, onImported }) {
  const [raw, setRaw] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const doImport = async () => {
    if (!raw.trim()) return toast.error("Paste some emails first");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/import-contacts`, { raw }, auth);
      setResult(data);
      if (data.added > 0) {
        toast.success(`Added ${data.added} new contact(s)`);
        setRaw("");
        onImported && onImported();
      } else {
        toast.info("No new contacts added (all duplicates or invalid)");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl" data-testid="import-panel">
      <div className="bg-white border border-black/5 p-8">
        <div className="flex items-center gap-3 mb-2">
          <Upload className="text-gold" size={22} />
          <h3 className="font-heading text-2xl">Import customer emails</h3>
        </div>
        <p className="text-[#52525b] font-light text-sm mb-5">
          Paste emails separated by commas, spaces, or new lines. Optionally add a name per line
          as <span className="font-mono text-xs">Name &lt;email&gt;</span> or <span className="font-mono text-xs">Name, email</span>.
          New contacts join your main list and are automatically included when you email "Everyone".
        </p>
        <textarea
          data-testid="import-textarea"
          rows={9}
          className={`${Input} resize-none font-mono text-sm`}
          placeholder={"jane@example.com\nJohn Smith <john@example.com>\nMaria Lopez, maria@example.com"}
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
        />
        <button data-testid="import-submit" onClick={doImport} disabled={busy} className="mt-4 w-full bg-[#0a0a0a] text-white py-4 text-sm tracking-wide hover:bg-[#27272a] transition-colors disabled:opacity-60">
          {busy ? "Importing..." : "Import Contacts"}
        </button>

        {result && (
          <div className="mt-6 grid grid-cols-3 gap-px bg-black/5" data-testid="import-result">
            <div className="bg-white p-5 text-center">
              <p className="font-heading text-3xl text-gold">{result.added}</p>
              <p className="eyebrow text-[10px] text-[#a1a1aa] mt-1">Added</p>
            </div>
            <div className="bg-white p-5 text-center">
              <p className="font-heading text-3xl">{result.duplicates}</p>
              <p className="eyebrow text-[10px] text-[#a1a1aa] mt-1">Duplicates</p>
            </div>
            <div className="bg-white p-5 text-center">
              <p className="font-heading text-3xl">{result.invalid}</p>
              <p className="eyebrow text-[10px] text-[#a1a1aa] mt-1">Invalid lines</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  if (!token) return <Login onLogin={setToken} />;
  return <Dashboard token={token} onLogout={logout} />;
}
